'use client';

/**
 * Primitive components for building custom pill section popup menus.
 * Render inside the DevPill Shadow DOM — styling comes from the pill stylesheet.
 */

import { ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface PillMenuItemProps {
  icon?: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'default' | 'purple' | 'amber';
}

/** Renders a single interactive button row inside a pill popup menu, supporting optional icon, label, disabled state, and color variant. */
export function PillMenuItem({
  icon,
  label,
  onClick,
  disabled,
  variant = 'default',
}: PillMenuItemProps): React.ReactNode {
  const variantClass = variant !== 'default' ? ` accent-${variant}` : '';
  return (
    <button
      type="button"
      className={`menu-item${disabled ? ' disabled' : ''}${variantClass}`}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

/** Renders a horizontal divider to visually separate groups of items within a pill popup menu. */
export function PillMenuSeparator(): React.ReactNode {
  return <div className="menu-separator" />;
}

interface PillMenuLinkProps {
  icon?: React.ReactNode;
  label: string;
  href: string;
}

/** Renders a menu row that navigates to an external URL in a new tab, styled identically to a PillMenuItem. */
export function PillMenuLink({ icon, label, href }: PillMenuLinkProps): React.ReactNode {
  return (
    <a className="menu-item" href={href} target="_blank" rel="noopener noreferrer">
      {icon}
      <span>{label}</span>
    </a>
  );
}

interface PillMenuGroupProps {
  icon?: React.ReactNode;
  label: string;
  children: React.ReactNode;
  onExpand?: () => void;
}

/**
 * Cascading flyout: hovering (or focusing) the trigger row reveals the
 * children in a side panel anchored to the left of the trigger. Mouseleave
 * from the wrapper closes the panel after a short delay so the user has
 * time to move into the panel without flicker.
 */
export function PillMenuGroup({
  icon,
  label,
  children,
  onExpand,
}: PillMenuGroupProps): React.ReactNode {
  const [expanded, setExpanded] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  /** Opens the flyout panel immediately, cancelling any pending close timer and firing the onExpand side effect once. */
  const open = useCallback(() => {
    if (closeTimerRef.current != null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    // Call onExpand outside the setState updater so StrictMode's double-invoke
    // of the updater doesn't fire the side effect twice.
    if (!expanded) onExpand?.();
    setExpanded(true);
  }, [expanded, onExpand]);

  /** Schedules the flyout to close after a short delay, giving the pointer time to travel from the trigger into the panel without flickering. */
  const scheduleClose = useCallback(() => {
    if (closeTimerRef.current != null) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setExpanded(false);
      closeTimerRef.current = null;
    }, 120);
  }, []);

  // Synchronous focus-containment check: when focus moves between the trigger
  // and a child menu item the blur fires with relatedTarget pointing at the
  // next focus target. If that target is still inside the wrapper, no close
  // is needed — bypass the 120ms timer so slow focus transitions (focus-trap
  // libs, screen readers, slow click handlers) don't race the timer.
  // relatedTarget is null when focus leaves to the document/window — treat
  // null as "outside" and fall through to scheduleClose.
  /** Handles focus leaving the wrapper, closing the flyout only when focus moves outside the group rather than between internal elements. */
  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      const next = event.relatedTarget as Node | null;
      if (next != null && wrapperRef.current?.contains(next)) {
        return;
      }
      scheduleClose();
    },
    [scheduleClose]
  );

  // Clear pending close timer on unmount so a focus/mouseleave-scheduled close
  // doesn't fire setState on an unmounted component.
  useEffect(() => {
    return () => {
      if (closeTimerRef.current != null) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, []);

  return (
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: hover-only wrapper that hosts a button trigger + flyout panel; the interactive elements live inside (the button trigger and the children as menu items). The wrapper exists solely for mouseenter/leave geometry on the cascade.
    <div
      className="menu-group-cascade"
      onBlur={handleBlur}
      onFocus={open}
      onMouseEnter={open}
      onMouseLeave={scheduleClose}
      ref={wrapperRef}
      role="group"
    >
      <button type="button" className={`menu-item${expanded ? ' active' : ''}`} onClick={open}>
        {icon}
        <span style={{ flex: 1 }}>{label}</span>
        <ChevronRight size={10} />
      </button>
      {expanded && (
        <div className="menu-group-cascade-panel" role="menu">
          {children}
        </div>
      )}
    </div>
  );
}
